import type { CategorizedDevice, ConsultationContext, DeviceState } from '../types'
import { getStethoscopes, getMedicalCameras } from '@/utils/devices'

/**
 * Creates a state aggregator that assembles consultation context and device state
 * from Redux store data and browser APIs.
 */
export function createStateAggregator(consultation: any, customDevices: any[]) {
    function getConsultationContext(consultationId: string): ConsultationContext {
        if (!consultation) {
            return { consultationId }
        }

        return {
            consultationId,
            patientId: consultation.patient?._id || consultation.patient,
            doctorId: consultation.medecin?._id || consultation.medecin,
            pdsId: consultation.pds?._id || consultation.pds,
            status: consultation.status,
            reason: consultation.reason,
        }
    }

    async function getDeviceState(): Promise<DeviceState> {
        let mediaDevices: MediaDeviceInfo[] = []
        try {
            if (typeof navigator !== 'undefined' && navigator.mediaDevices) {
                mediaDevices = await navigator.mediaDevices.enumerateDevices()
            }
        } catch {
            // Silently handle if device enumeration fails
        }

        const safeCustomDevices = Array.isArray(customDevices) ? customDevices : []

        // Categorize devices using the same logic as the RTC provider
        const stethoscopeDevices = getStethoscopes(safeCustomDevices, mediaDevices)
        const medicalCameraDevices = getMedicalCameras(safeCustomDevices, mediaDevices)

        const stethoscopeDeviceIds = new Set(stethoscopeDevices.map((d: any) => d.device?.deviceId))
        const medicalCameraDeviceIds = new Set(medicalCameraDevices.map((d: any) => d.device?.deviceId))

        const toCategorized = (d: any): CategorizedDevice => ({
            deviceId: d.device?.deviceId || d.deviceId,
            label: d.device?.name || d.label || 'Unknown',
            customName: d.customName,
            kind: d.device?.type || d.kind || 'unknown',
        })

        const stethoscopes = stethoscopeDevices.map(toCategorized)
        const medicalCameras = medicalCameraDevices.map(toCategorized)

        // Remaining audio/video inputs that aren't identified as medical devices
        const audioInputs = mediaDevices
            .filter((d) => d.kind === 'audioinput' && !stethoscopeDeviceIds.has(d.deviceId))
            .map((d) => ({ deviceId: d.deviceId, label: d.label, kind: d.kind }))

        const videoInputs = mediaDevices
            .filter((d) => d.kind === 'videoinput' && !medicalCameraDeviceIds.has(d.deviceId))
            .map((d) => ({ deviceId: d.deviceId, label: d.label, kind: d.kind }))

        const mapped = safeCustomDevices.map((d: any) => ({
            id: d._id || d.id,
            name: d.name || d.label || 'Unknown device',
            type: d.type || 'unknown',
            available: d.available !== false,
        }))

        return {
            stethoscopes,
            medicalCameras,
            audioInputs,
            videoInputs,
            customDevices: mapped,
        }
    }

    return { getConsultationContext, getDeviceState }
}
